import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileDown, Edit3 } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const enconOrange = '#ee7203';

const sumLineCosts = (suppliers) => {
  let total = 0;
  (suppliers || []).forEach(s => (s.adjustment_lines || []).forEach(ln => {
    const v = parseFloat(ln.cost);
    if (!isNaN(v)) total += v;
  }));
  return total;
};

const fmtTitleDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${d.toLocaleString('en-GB', { month: 'long' })} ${d.getDate()} ${d.getFullYear()}`;
  } catch { return iso; }
};

const PIReportViewer = ({ user }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoStatus, setAutoStatus] = useState('idle');
  const dirtyRef = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`${API}/pi-counts/reports/${reportId}`);
        setReport(res.data);
      } catch (e) { toast.error('Failed to load report'); }
      finally { setLoading(false); }
    })();
  }, [reportId]);

  const effectiveTotal = (() => {
    if (!report) return 0;
    const ov = report.stats?.total_adjustment_value_override;
    if (ov !== null && ov !== undefined && ov !== '') {
      const v = parseFloat(ov);
      if (!isNaN(v)) return v;
    }
    return sumLineCosts(report.suppliers);
  })();

  const flatAdjustments = (() => {
    if (!report) return [];
    const lines = [];
    (report.suppliers || []).forEach(s => (s.adjustment_lines || []).forEach((ln, i) => {
      lines.push({ ...ln, supplier_index: report.suppliers.indexOf(s), line_index: (s.adjustment_lines || []).indexOf(ln) });
    }));
    return lines;
  })();

  const markDirty = () => {
    dirtyRef.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!dirtyRef.current || !report) return;
      dirtyRef.current = false;
      setAutoStatus('saving');
      try {
        const lineSum = sumLineCosts(report.suppliers);
        const stats = { ...report.stats, total_adjustment_value: lineSum };
        await api.put(`${API}/pi-counts/reports/${report.id}`, {
          name: report.name,
          summary_text: report.summary_text,
          stats,
          suppliers: report.suppliers,
          upcoming_scheduled: report.upcoming_scheduled,
        });
        setAutoStatus('saved');
        setTimeout(() => setAutoStatus(prev => prev === 'saved' ? 'idle' : prev), 1500);
      } catch (e) { setAutoStatus('idle'); toast.error('Auto-save failed'); }
    }, 800);
  };

  const updateField = (key, value) => {
    setReport(prev => ({ ...prev, [key]: value }));
    markDirty();
  };
  const updateStat = (key, value) => {
    setReport(prev => ({ ...prev, stats: { ...prev.stats, [key]: value } }));
    markDirty();
  };
  const updateAdjustmentLine = (supplierIdx, lineIdx, key, value) => {
    setReport(prev => {
      const next = { ...prev };
      const sups = [...(next.suppliers || [])];
      const sup = { ...sups[supplierIdx] };
      const lines = [...(sup.adjustment_lines || [])];
      lines[lineIdx] = { ...lines[lineIdx], [key]: value };
      sup.adjustment_lines = lines;
      sups[supplierIdx] = sup;
      next.suppliers = sups;
      return next;
    });
    markDirty();
  };

  const handleManualSave = async () => {
    if (!report) return;
    setSaving(true);
    try {
      const lineSum = sumLineCosts(report.suppliers);
      const stats = { ...report.stats, total_adjustment_value: lineSum };
      await api.put(`${API}/pi-counts/reports/${report.id}`, {
        name: report.name, summary_text: report.summary_text, stats,
        suppliers: report.suppliers, upcoming_scheduled: report.upcoming_scheduled,
      });
      toast.success('Report saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handlePdf = async () => {
    try {
      const res = await api.get(`${API}/pi-counts/reports/${report.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(report.name || 'report').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF export failed'); }
  };

  const subHeaderLeft = (
    <button
      onClick={() => navigate('/pi-counts')}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
      style={{ color: enconOrange }}
      data-testid="report-back-btn"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Back</span>
    </button>
  );
  const subHeaderRight = (
    <>
      {autoStatus !== 'idle' && (
        <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${autoStatus === 'saving' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
          {autoStatus === 'saving' ? 'Saving…' : 'Auto-saved'}
        </span>
      )}
      <button
        onClick={handleManualSave}
        disabled={saving}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
        style={{ color: enconOrange }}
        data-testid="report-save-btn"
      >
        <Save className="w-3.5 h-3.5" />
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button
        onClick={handlePdf}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-white"
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
        data-testid="report-pdf-btn"
      >
        <FileDown className="w-3.5 h-3.5" />
        Export PDF
      </button>
    </>
  );

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
      <p className="text-center pt-32 text-gray-500">Loading report…</p>
    </div>
  );
  if (!report) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader user={user} subHeaderLeft={subHeaderLeft} subHeaderRight={subHeaderRight} />
      <div className="px-4 lg:px-8 py-6" style={{ paddingTop: '120px' }}>
        <div className="max-w-[1400px] mx-auto">
          {/* Title — single line */}
          <div className="bg-white rounded-2xl border-2 p-5 mb-4" style={{ borderColor: enconOrange }}>
            <h1 className="text-2xl md:text-3xl font-bold whitespace-nowrap overflow-x-auto" style={{ color: enconOrange }} data-testid="report-title-line">
              PI Counts Report&nbsp;&nbsp;{fmtTitleDate(report.start_date)} to {fmtTitleDate(report.end_date)}
            </h1>
            <Input
              value={report.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              className="text-sm border-0 px-0 mt-1 focus-visible:ring-0 text-gray-500"
              data-testid="report-name-input"
              placeholder="Internal report name"
            />
          </div>

          {/* 4 stat boxes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Total Suppliers Counted', value: report.stats?.total_suppliers || 0, color: '#ee7203' },
              { label: 'Total Counts', value: report.stats?.total_counts || 0, color: '#3b82f6' },
              { label: 'Total Adjustments', value: report.stats?.total_adjustments || 0, color: '#0d9488' },
              { label: 'Total Adjustment Value', value: null, color: '#dc2626', isMoney: true },
            ].map((box, i) => (
              <div key={i} className="bg-white rounded-2xl border-2 p-4 text-center" style={{ borderColor: box.color }}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: box.color }}>{box.label}</p>
                {box.isMoney ? (
                  <div>
                    <p className="text-3xl font-bold" style={{ color: box.color }}>£{(effectiveTotal || 0).toFixed(2)}</p>
                    <Input
                      type="number" step="0.01"
                      placeholder="Override total…"
                      value={report.stats?.total_adjustment_value_override ?? ''}
                      onChange={(e) => updateStat('total_adjustment_value_override', e.target.value === '' ? null : e.target.value)}
                      className="text-xs h-8 mt-2 text-center"
                      data-testid="stat-total-override"
                    />
                  </div>
                ) : (
                  <p className="text-3xl font-bold" style={{ color: box.color }}>{box.value}</p>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 mb-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5 mb-2">
              <Edit3 className="w-3 h-3" />
              Summary
            </Label>
            <textarea
              value={report.summary_text || ''}
              onChange={(e) => updateField('summary_text', e.target.value)}
              rows={4}
              placeholder="Write your summary…"
              className="w-full p-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-violet-400"
              data-testid="report-summary-textarea"
            />
          </div>

          {/* Adjustment Summary — flat, no supplier headers */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 mb-4">
            <h2 className="text-lg font-bold text-teal-700 mb-3" data-testid="adjustment-summary-heading">Adjustment Summary</h2>
            {flatAdjustments.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No adjustments in this window.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-teal-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Code</th>
                      <th className="px-2 py-1.5 text-left">Description</th>
                      <th className="px-2 py-1.5 text-center">Counted</th>
                      <th className="px-2 py-1.5 text-right">Final</th>
                      <th className="px-2 py-1.5 text-right">Disc</th>
                      <th className="px-2 py-1.5 text-right w-32">Cost (£)</th>
                      <th className="px-2 py-1.5 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {flatAdjustments.map((ln, i) => (
                      <tr key={i} data-testid={`adjustment-row-${i}`}>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{ln.product_code}</td>
                        <td className="px-2 py-1.5">{ln.description}</td>
                        <td className="px-2 py-1.5 text-center text-gray-700">
                          Count ({ln.count_times || 0} time{(ln.count_times || 0) === 1 ? '' : 's'})
                        </td>
                        <td className="px-2 py-1.5 text-right">{ln.physical_count ?? ''}</td>
                        <td className={`px-2 py-1.5 text-right font-semibold ${(ln.discrepancy || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{ln.discrepancy}</td>
                        <td className="px-1 py-1">
                          <Input
                            type="number" step="0.01"
                            value={ln.cost ?? 0}
                            onChange={(e) => updateAdjustmentLine(ln.supplier_index, ln.line_index, 'cost', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            className="h-7 text-xs text-right"
                            data-testid={`adj-cost-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-[11px]">{ln.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Suppliers Counted — name + count only */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 mb-4">
            <h2 className="text-lg font-bold mb-3" style={{ color: enconOrange }} data-testid="suppliers-counted-heading">Suppliers Counted</h2>
            {(report.suppliers || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic">No counts in this window.</p>
            ) : (
              <ul className="divide-y">
                {report.suppliers.map((s, i) => (
                  <li key={s.name} className="flex items-center justify-between py-2" data-testid={`supplier-row-${i}`}>
                    <span className="font-semibold text-gray-800">{s.name}</span>
                    <span className="text-sm text-gray-600">{s.count_total || 0} count{s.count_total === 1 ? '' : 's'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Upcoming Scheduled */}
          {(report.upcoming_scheduled || []).length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-red-200 p-4 mb-4">
              <h2 className="text-lg font-bold text-red-700 mb-3" data-testid="upcoming-scheduled-heading">Upcoming Scheduled</h2>
              <div className="space-y-3">
                {report.upcoming_scheduled.map((u) => (
                  <div key={u.id} className="border border-red-200 rounded-lg p-3 bg-red-50/30" data-testid={`upcoming-row-${u.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <p className="font-semibold text-gray-900">{u.supplier_name}</p>
                      <p className="text-xs text-red-700 font-semibold">Due {u.date} · {u.items} item{u.items === 1 ? '' : 's'}</p>
                    </div>
                    {(u.products || []).length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead className="bg-red-100">
                            <tr>
                              <th className="px-2 py-1 text-left">Code</th>
                              <th className="px-2 py-1 text-left">Description</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {u.products.map((p, j) => (
                              <tr key={j}>
                                <td className="px-2 py-1 font-mono">{p.code}</td>
                                <td className="px-2 py-1">{p.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PIReportViewer;
